import { Build } from "@stencil/core";
import { combineLatest, from } from "rxjs";
import { switchMap, map } from "rxjs/operators";
import { setState, state } from "..";
import { APP_CONFIG } from "../../config";
import { reportError } from "../error/effects";
import { DeviceState } from "./state";
import { ProfileStatus } from "../../services/User/User.interface";
import { Cookies } from "../../services/Device/Device.interface";
import { storageSet } from "../../services/Device/storage";
import {
  isInstalled,
  loadCookiesObservable,
} from "../../services/Device/device";
import { updateAuthState } from "../user/effects";
import { NotificationsManager } from "../../services/Device/notifications";
import { onNetWorkStatusChanged } from "../../services/Device/system";

function updateDeviceState(data: Partial<DeviceState>, effect: string) {
  setState("DEVICE", { DEVICE: { ...state.DEVICE, ...data } }, effect);
}

async function initializeSentry() {
  const { logger } = await import("../../utils/logger");
  if (Build.isBrowser) {
    const { initSentry } = await import("../../utils/sentry");
    initSentry({
      dsn: APP_CONFIG.sentry.dsn,
      environment: APP_CONFIG.environment,
      debug: false,
    });
    logger.info("INIT: Initialized sentry");
  }
  logger.info("INIT: Sentry init skipped, pre-render build");
}

async function updateNavigation(
  isStandalone: boolean,
  profileStatus: ProfileStatus,
  effect: string
) {
  const { computeAppNavigation, computeSiteNavigation } = await import(
    "../../services/Navigation"
  );
  const { updateDeviceNavigation } = await import("../navigation/effects");

  const { logger } = await import("../../utils/logger");

  if (isStandalone) {
    updateDeviceNavigation(computeAppNavigation(profileStatus), effect);
  } else {
    updateDeviceNavigation(computeSiteNavigation(), effect);
  }
  logger.info("INIT: Initialized navigation");
}

function initializeApp() {
  const effect = "initializeApp";
  return combineLatest([
    import("../../services/Device/device"),
    from(import("../../services/User/User")),
    from(import("../../services/Device/notifications")),
  ])
    .pipe(
      switchMap(([{ deviceInfoObservable }, { userAuthObservable }]) => {
        return deviceInfoObservable().pipe(
          switchMap((deviceInfo) =>
            combineLatest([
              loadCookiesObservable(),
              from(isInstalled()),
              from(initializeSentry()),
            ]).pipe(
              switchMap(([cookies, installed]) => {
                return userAuthObservable(deviceInfo.languageCode).pipe(
                  map((user) => ({
                    deviceInfo: {
                      ...deviceInfo,
                      ...cookies,
                      notificationsEnabled: user.notifications === "enabled",
                    },
                    user,
                    installed,
                  }))
                );
              })
            )
          )
        );
      })
    )
    .subscribe({
      next({ deviceInfo, user, installed }) {
        updateAuthState(user);
        updateNavigation(installed, user.status, effect).then(() => {
          updateDeviceState(
            {
              ...deviceInfo,
              languageCode:
                deviceInfo.standalone && user?.userLanguage
                  ? user.userLanguage
                  : deviceInfo.languageCode,
              initialized: true,
            },
            effect
          );
        });
      },
      error(err) {
        reportError("DEVICE", err, effect);
        updateDeviceState({ initialized: true }, effect);
      },
    });
}

async function updateInstallationStatus(status: boolean) {
  const effect = "updateInstallationStatus";
  updateDeviceState(
    {
      installed: status,
    },
    effect
  );
  await storageSet("cookies", {
    installed: state.DEVICE.installed,
    cookieAgreement: state.DEVICE.cookieAgreement,
  } as Cookies);
}

async function updateCookieAgreement(status: boolean) {
  const effect = "updateCookieAgreement";
  updateDeviceState(
    {
      cookieAgreement: status,
    },
    effect
  );
  await storageSet("cookies", {
    installed: state.DEVICE.installed,
    cookieAgreement: state.DEVICE.cookieAgreement,
  } as Cookies);
}

async function checkNotifications() {
  const notificationsManager = new NotificationsManager();
  const isEnabled = await notificationsManager.isEnabled();
  return isEnabled;
}

function enableNotificationsEffect() {
  const effect = "updateNotificationsStatus";
  return from(new NotificationsManager().enableNativeNotifications()).subscribe(
    {
      error(err) {
        reportError("DEVICE", err, effect);
      },
    }
  );
}

function updateNetworkStatusEffect(
) {
  const effect = "updateNetworkStatusEffect";
  return onNetWorkStatusChanged().subscribe(
    {
      next(networkStatus) {
        updateDeviceState({ networkStatus }, effect);
      },
      error(err) {
        reportError("DEVICE", err, effect);
      },
    }
  )

}

export {
  updateDeviceState,
  initializeApp,
  updateInstallationStatus,
  updateCookieAgreement,
  enableNotificationsEffect,
  updateNetworkStatusEffect,
  checkNotifications,
};
